import { Alert, AlertDescription, AlertTitle } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { NotPermitted } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { useCan } from '@/domains/auth';
import type { IWorkItemRow, RemovalReason, WorkItemDecisionCode } from '../../api/work-item-contracts';
import { workItemsApi } from '../../api/work-items-api';
import { SelectionBar } from '../lists/work-page/selection-bar';
import { escalateBlocked, lastLevelOf, useEscalationLevels } from './escalation';
import { sentence, unanswered } from '../person-hub/said';
import {
  decisionLabel,
  sharedDecisions,
  sharedRemovalReason,
  transitionRefusal,
  workItemName
} from '../../work-items';
import {
  DecisionDialog,
  DecisionPresses,
  NOT_PERMITTED_DECIDE,
  controlRefusal,
  decisionBody,
  memberOf
} from './work-item-writes';

/**
 * One decision over every item ticked on a list (SR-222, F9's R7; W12) — the compliance queue's
 * and My tasks'. The same route and the same dialog as a single decision in the pane: survey-api's
 * `POST /admin/work-items/decisions` takes 1–200 ids, **all or nothing**, with no `If-Match`.
 *
 * **Only a decision every ticked item takes is offered** (`sharedDecisions` over
 * `WORK_ITEM_MOVES`, the mirror, narrowed per item by its trigger and member): a press that
 * survey-api would refuse for one of them is not drawn, and none offered is said as a state, the
 * why under the page's "?". The selection is the list's, per query — a change of tab, filter or
 * page clears it, so it never holds more than one page (≤ 100 < survey-api's 200) — and only what
 * the page still draws is sent: an item a re-read took out of the view drops out of the
 * selection's count.
 *
 * **What came back.** A decision that landed clears the selection and the list reads again, with
 * the summary. The rationale is required on Remove only, a blank one left out of the body. A
 * problem at the rationale or the follow-up day (400) is said inside the dialog, as in the pane.
 * The rest closes the dialog and is said above the bar, since nothing was decided and the fix is
 * in the selection:
 *
 * - 422 `invalid-action` names, one problem per id, the items that no longer exist: they are said
 *   by name (`workItemName`: subject and whose it is — a rule item's subject is its criterion's,
 *   shared by every item it raised) and unticked, the rest kept for the press again;
 * - 409 `invalid-transition` names every item whose state refused the decision: said by name,
 *   in survey-api's words, the selection kept and the list read again, so the states are current;
 * - 409 `revision-conflict` — the batch lost a race — and an answer that never arrived read the
 *   list again and ask for it to be checked.
 *
 * survey-api's 422 carries each id only in its problem's reason (`"<id> names no work item."`), so
 * a ticked id is matched by appearing there — the one place the wire puts it.
 */
export function BulkDecisions({
  asked,
  rows,
  onSelect,
  onDecided,
  stickyTop,
  extra
}: {
  /** The query the list answers: what was said is said under it alone, as the selection is. */
  asked: string;
  /** The ticked rows the page still draws. */
  rows: IWorkItemRow[];
  /** The selection replaced — by these ids. */
  onSelect: (ids: string[]) => void;
  /** Read the list (and whatever counts it) again. */
  onDecided: () => void;
  /** Where the page scrolls: the selection bar sticks this far under the window's top. */
  stickyTop?: string;
  /** Drawn on the bar before the decisions — the queue's Assign (SR-466). */
  extra?: React.ReactNode;
}) {
  const [open, setOpen] = React.useState<Nullable<WorkItemDecisionCode>>(null);
  // The selection stays without `work_items.decide` — ticking decides nothing — and the presses
  // are drawn disabled with the reason (SR-228's R3).
  const permitted = useCan()('work_items.decide');
  const reasonId = React.useId();
  const [saidFor, setSaidFor] = React.useState<
    Nullable<{ asked: string; refused: boolean; title: string; text: string }>
  >(null);
  // A press's outcome belongs to the query it was made under: another tab, filter or page shows
  // other rows, and the sentence about these would sit above them.
  const said = saidFor?.asked === asked ? saidFor : null;
  const setSaid = (note: Nullable<{ refused: boolean; title: string; text: string }>) =>
    setSaidFor(note && { asked, ...note });
  const decisions = sharedDecisions(rows);
  const atLast = escalateBlocked(rows, lastLevelOf(useEscalationLevels()));
  const about = rows.length === 1 ? workItemName(rows[0]) : `${rows.length} items`;

  const decide = async (
    decision: WorkItemDecisionCode,
    rationale: string,
    followUpOn?: string,
    removalReason?: RemovalReason
  ) => {
    const sent = rows;
    setSaid(null);
    try {
      await workItemsApi.decideWorkItems(
        decisionBody(sent.map((row) => row.id), decision, rationale, followUpOn, removalReason)
      );
    } catch (error) {
      return trouble(error, sent);
    }
    onSelect([]);
    setSaid({
      refused: false,
      title: `${decisionLabel(decision)} — done`,
      text: `${countOf(sent.length)} decided. The list is read again.`
    });
    onDecided();
    return null;
  };

  /** A refusal of the batch: the dialog's to say (`string`), or said here (`null`). */
  const trouble = (error: unknown, sent: IWorkItemRow[]): Nullable<string> => {
    /** Said above the bar, with the list read again. */
    const sayAndReread = (title: string, text: string) => {
      setSaid({ refused: true, title, text });
      onDecided();
      return null;
    };
    const refused = transitionRefusal(error);
    if (refused) {
      const ids = refused.itemIds.map((id) => id.toLowerCase());
      const named = sent.filter((row) => ids.includes(row.id.toLowerCase()));
      return sayAndReread(
        'Nothing was decided',
        `${sentence(refused.reason ?? 'The decision was refused for an item’s state')} ` +
          (named.length > 0
            ? `Refused: ${names(named)}. Untick ${named.length === 1 ? 'it' : 'them'} and press ` +
              'again — an item the list no longer shows is left out by itself.'
            : 'The refusal named none of the items ticked.') +
          ' The list is read again.'
      );
    }
    if (error instanceof ApiError && error.isProblem(ProblemType.invalidAction)) {
      const reasons = (error.problem?.problems ?? [])
        .filter((problem) => problem.target === 'itemIds')
        .map((problem) => problem.reason.toLowerCase());
      const gone = sent.filter((row) => reasons.some((reason) => reason.includes(row.id.toLowerCase())));
      if (gone.length > 0) {
        onSelect(sent.filter((row) => !gone.includes(row)).map((row) => row.id));
        return sayAndReread(
          'Nothing was decided',
          `${countOf(gone.length)} no longer ${gone.length === 1 ? 'exists' : 'exist'}: ` +
            `${names(gone)}. ${gone.length === 1 ? 'It has' : 'They have'} been unticked — press ` +
            'again for the rest.'
        );
      }
    }
    if (error instanceof ApiError && error.isProblem(ProblemType.revisionConflict)) {
      return sayAndReread(
        'Nothing was decided',
        'Another change to one of these items landed at the same moment. The list has been ' +
          'read again — check the items and press again.'
      );
    }
    if (unanswered(error)) {
      return sayAndReread(
        'No answer',
        'The answer did not arrive — the list has been read again: check whether the items ' +
          'changed before pressing again.'
      );
    }
    return controlRefusal(error);
  };

  const presses =
    decisions.length > 0 ? (
      <>
        <DecisionPresses
          decisions={decisions}
          onPress={setOpen}
          disabledFor={permitted ? undefined : reasonId}
          blocked={atLast ? { escalate: atLast } : undefined}
        />
        {!permitted && <NotPermitted id={reasonId} what={NOT_PERMITTED_DECIDE} />}
      </>
    ) : (
      <span className="text-muted-foreground">No decision is open to every selected item</span>
    );
  const dialog = (
    <DecisionDialog
      decision={open}
      about={about}
      member={rows.length === 1 ? memberOf(rows[0]) : undefined}
      removalDefault={sharedRemovalReason(rows)}
      onClose={() => setOpen(null)}
      onDecide={decide}
    />
  );

  return (
    <>
      {said && (
        <Alert variant={said.refused ? 'destructive' : 'default'} role={said.refused ? 'alert' : 'status'}>
          <AlertTitle>{said.title}</AlertTitle>
          <AlertDescription>{said.text}</AlertDescription>
        </Alert>
      )}
      <SelectionBar count={rows.length} onClear={() => onSelect([])} stickyTop={stickyTop}>
        {extra}
        {presses}
        {dialog}
      </SelectionBar>
    </>
  );
}

function countOf(count: number): string {
  return count === 1 ? '1 item' : `${count} items`;
}

function names(rows: IWorkItemRow[]): string {
  return rows.map((row) => `“${workItemName(row)}”`).join(', ');
}

/**
 * A list's selection, kept per query: the ids ticked under the query they were ticked under, and
 * nothing under any other — so a change of tab, filter or page starts with none, without an effect
 * that would draw the old selection for a frame first. `idOf` is the row's key — the work item's
 * id here, the response's on the recycle bin's list.
 */
export function useQuerySelection<Row>(
  asked: string,
  rows: readonly Row[],
  idOf: (row: Row) => string
) {
  const [picked, setPicked] = React.useState<{ asked: string; ids: string[] }>({ asked, ids: [] });
  const ids = picked.asked === asked ? picked.ids : [];
  const selected = React.useMemo(() => new Set(ids), [ids]);
  const onSelect = React.useCallback((next: string[]) => setPicked({ asked, ids: next }), [asked]);
  return {
    selected,
    onSelect,
    /** The ticked rows the page still draws — what a decision is sent for. */
    rows: rows.filter((row) => selected.has(idOf(row)))
  };
}
